<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import whatsappCallHistory from 'dashboard/api/whatsappCallHistory';
import { useTrack } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  contactId: {
    type: [Number, String],
    default: null,
  },
});

const { t, locale } = useI18n();

const calls = ref([]);
const isLoading = ref(false);
const isLoadingMore = ref(false);
const hasLoadingError = ref(false);
const hasPlaybackError = ref(false);
const activeCallId = ref(null);
const playbackState = ref('stopped');
const currentPage = ref(0);
const totalCallCount = ref(null);

let activeAudio = null;
let activeAudioUrl = null;
let requestVersion = 0;
let playbackRequestVersion = 0;

const CALL_HISTORY_EVENTS = Object.freeze({
  PLAYED: 'WhatsApp call history recording played',
  PAUSED: 'WhatsApp call history recording paused',
  STOPPED: 'WhatsApp call history recording stopped',
});

const trackPlayback = (event, call) => {
  useTrack(event, {
    conversation_id: props.conversationId,
    call_id: call.id,
    source: 'contact_panel',
  });
};

const callCount = computed(() => calls.value.length);
const hasMoreCalls = computed(
  () =>
    Number.isInteger(totalCallCount.value) &&
    calls.value.length < totalCallCount.value
);

const extractHistory = response => {
  const payload = response?.data;

  if (Array.isArray(payload)) return { calls: payload, meta: {} };
  if (Array.isArray(payload?.data)) {
    return { calls: payload.data, meta: payload.meta || {} };
  }
  if (Array.isArray(payload?.calls)) {
    return { calls: payload.calls, meta: payload.meta || {} };
  }
  if (Array.isArray(payload?.whatsapp_calls)) {
    return { calls: payload.whatsapp_calls, meta: payload.meta || {} };
  }

  return { calls: [], meta: {} };
};

const normalizeCall = (call, index) => ({
  ...call,
  id: call.id ?? `${call.created_at ?? call.started_at ?? index}-${index}`,
  recordedAt:
    call.started_at ??
    call.startedAt ??
    call.initiated_at ??
    call.initiatedAt ??
    call.called_at ??
    call.calledAt ??
    call.created_at,
  endedAt:
    call.ended_at ?? call.endedAt ?? call.completed_at ?? call.completedAt,
  durationSeconds:
    call.duration_seconds ??
    call.durationSeconds ??
    call.metadata?.duration_seconds ??
    call.metadata?.durationSeconds ??
    call.duration,
  recordingUrl:
    call.recording_url ??
    call.recordingUrl ??
    call.audio_url ??
    call.audioUrl ??
    call.media_url ??
    call.mediaUrl ??
    call.recording?.url ??
    null,
});

const formatDateTime = value => {
  if (!value || Number.isNaN(new Date(value).getTime())) {
    return t('CONVERSATION.CALL_HISTORY.DATE_UNAVAILABLE');
  }

  return new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const durationInSeconds = call => {
  const duration = Number(call.durationSeconds);
  if (Number.isFinite(duration) && duration >= 0) return Math.floor(duration);

  const startedAt = new Date(call.recordedAt).getTime();
  const endedAt = new Date(call.endedAt).getTime();

  if (
    Number.isFinite(startedAt) &&
    Number.isFinite(endedAt) &&
    endedAt >= startedAt
  ) {
    return Math.floor((endedAt - startedAt) / 1000);
  }

  return null;
};

const formatDuration = call => {
  const seconds = durationInSeconds(call);
  if (seconds === null) {
    return t('CONVERSATION.CALL_HISTORY.DURATION_UNAVAILABLE');
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(remainingSeconds).padStart(2, '0');

  return hours > 0
    ? `${String(hours).padStart(2, '0')}:${paddedMinutes}:${paddedSeconds}`
    : `${paddedMinutes}:${paddedSeconds}`;
};

const statusLabel = status => {
  const normalizedStatus = String(status || 'unknown').toUpperCase();
  const translationKey = `CONVERSATION.CALL_HISTORY.STATUS.${normalizedStatus}`;
  const translated = t(translationKey);

  return translated === translationKey
    ? t('CONVERSATION.CALL_HISTORY.STATUS.UNKNOWN')
    : translated;
};

const releaseActiveAudioUrl = () => {
  if (!activeAudioUrl) return;

  URL.revokeObjectURL(activeAudioUrl);
  activeAudioUrl = null;
};

const handleAudioEnded = () => {
  activeAudio = null;
  releaseActiveAudioUrl();
  activeCallId.value = null;
  playbackState.value = 'stopped';
};

const handleAudioError = () => {
  hasPlaybackError.value = true;
  handleAudioEnded();
};

const stopPlayback = () => {
  playbackRequestVersion += 1;
  if (activeAudio) {
    activeAudio.pause();
    activeAudio.currentTime = 0;
    activeAudio.removeEventListener('ended', handleAudioEnded);
    activeAudio.removeEventListener('error', handleAudioError);
    activeAudio = null;
  }
  releaseActiveAudioUrl();

  activeCallId.value = null;
  playbackState.value = 'stopped';
};

const resumePlayback = async (call, audio) => {
  try {
    await audio.play();

    if (activeAudio !== audio || activeCallId.value !== call.id) return;

    playbackState.value = 'playing';
    trackPlayback(CALL_HISTORY_EVENTS.PLAYED, call);
  } catch {
    if (activeAudio !== audio) return;

    hasPlaybackError.value = true;
    stopPlayback();
  }
};

const playCall = async call => {
  if (!call.recordingUrl) return;

  hasPlaybackError.value = false;

  if (activeCallId.value === call.id && activeAudio) {
    await resumePlayback(call, activeAudio);
    return;
  }

  stopPlayback();
  const currentPlaybackRequestVersion = playbackRequestVersion;
  activeCallId.value = call.id;
  playbackState.value = 'loading';

  try {
    const response = await whatsappCallHistory.getRecording(call.recordingUrl);
    if (
      currentPlaybackRequestVersion !== playbackRequestVersion ||
      activeCallId.value !== call.id
    ) {
      return;
    }

    activeAudioUrl = URL.createObjectURL(response.data);
    activeAudio = new Audio(activeAudioUrl);
    activeAudio.addEventListener('ended', handleAudioEnded);
    activeAudio.addEventListener('error', handleAudioError);
    await resumePlayback(call, activeAudio);
  } catch {
    if (currentPlaybackRequestVersion !== playbackRequestVersion) return;

    hasPlaybackError.value = true;
    stopPlayback();
  }
};

const pauseCall = call => {
  if (activeCallId.value !== call.id || !activeAudio) return;

  activeAudio.pause();
  playbackState.value = 'paused';
  trackPlayback(CALL_HISTORY_EVENTS.PAUSED, call);
};

const isActive = call => activeCallId.value === call.id;
const isPlaying = call => isActive(call) && playbackState.value === 'playing';
const isLoadingPlayback = call =>
  isActive(call) && playbackState.value === 'loading';

const stopCall = call => {
  if (!isActive(call)) return;

  stopPlayback();
  trackPlayback(CALL_HISTORY_EVENTS.STOPPED, call);
};

const loadHistory = async ({ append = false } = {}) => {
  if (isLoading.value) return;

  requestVersion += 1;
  const currentRequestVersion = requestVersion;
  const contactId = props.contactId;
  const nextPage = append ? currentPage.value + 1 : 1;

  if (!append) {
    calls.value = [];
    totalCallCount.value = null;
    currentPage.value = 0;
    hasLoadingError.value = false;
    hasPlaybackError.value = false;
  }

  if (!contactId) {
    isLoading.value = false;
    isLoadingMore.value = false;
    return;
  }

  isLoading.value = true;
  isLoadingMore.value = append;

  try {
    const response = await whatsappCallHistory.get(contactId, {
      page: nextPage,
      per_page: 100,
    });

    if (currentRequestVersion !== requestVersion) return;

    const history = extractHistory(response);
    const nextCalls = history.calls.map(normalizeCall);
    calls.value = append ? [...calls.value, ...nextCalls] : nextCalls;
    currentPage.value = nextPage;
    totalCallCount.value = Number.isInteger(history.meta.total_count)
      ? history.meta.total_count
      : calls.value.length;
  } catch {
    if (currentRequestVersion === requestVersion) {
      hasLoadingError.value = true;
    }
  } finally {
    if (currentRequestVersion === requestVersion) {
      isLoading.value = false;
      isLoadingMore.value = false;
    }
  }
};

const refreshHistory = () => loadHistory();
const loadMoreHistory = () => loadHistory({ append: true });

watch(
  () => [props.conversationId, props.contactId],
  () => {
    stopPlayback();
    loadHistory();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  requestVersion += 1;
  stopPlayback();
});
</script>

<template>
  <section
    class="rounded-lg border border-n-weak bg-n-solid-1 p-3 sm:p-4"
    data-testid="whatsapp-call-history"
    :aria-busy="isLoading"
    :aria-label="$t('CONVERSATION.CALL_HISTORY.TITLE')"
  >
    <div class="flex items-center justify-between gap-3">
      <div class="min-w-0">
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ $t('CONVERSATION.CALL_HISTORY.TITLE') }}
        </h3>
        <p class="mt-1 text-xs text-n-slate-11">
          {{
            $t('CONVERSATION.CALL_HISTORY.COUNT', {
              count: callCount,
            })
          }}
        </p>
      </div>
      <Button
        v-tooltip.top="$t('CONVERSATION.CALL_HISTORY.REFRESH')"
        data-testid="whatsapp-call-history-refresh"
        ghost
        slate
        xs
        icon="i-ph-arrows-clockwise-bold"
        :aria-label="$t('CONVERSATION.CALL_HISTORY.REFRESH')"
        :disabled="isLoading"
        @click="refreshHistory"
      />
    </div>

    <div
      v-if="isLoading"
      class="flex items-center gap-2 py-6 text-sm text-n-slate-11"
      role="status"
    >
      <span class="i-ph-spinner-gap-bold animate-spin" aria-hidden="true" />
      {{ $t('CONVERSATION.CALL_HISTORY.LOADING') }}
    </div>

    <div
      v-else-if="hasLoadingError"
      class="flex flex-col items-start gap-3 py-5 text-sm text-n-ruby-11"
      role="alert"
    >
      <span>{{ $t('CONVERSATION.CALL_HISTORY.ERROR') }}</span>
      <Button
        data-testid="whatsapp-call-history-retry"
        xs
        outline
        ruby
        :label="$t('CONVERSATION.CALL_HISTORY.RETRY')"
        @click="refreshHistory"
      />
    </div>

    <p
      v-else-if="!callCount"
      class="py-6 text-sm text-n-slate-11"
      data-testid="whatsapp-call-history-empty"
    >
      {{ $t('CONVERSATION.CALL_HISTORY.EMPTY') }}
    </p>

    <ul v-else class="mt-4 space-y-3">
      <li
        v-for="call in calls"
        :key="call.id"
        class="rounded-md border border-n-weak bg-n-solid-2 p-3"
        data-testid="whatsapp-call-history-item"
      >
        <div class="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
          <p
            class="text-sm font-medium text-n-slate-12"
            :data-testid="`whatsapp-call-history-date-${call.id}`"
          >
            {{ formatDateTime(call.recordedAt) }}
          </p>
          <p
            class="text-xs text-n-slate-11"
            :data-testid="`whatsapp-call-history-duration-${call.id}`"
          >
            {{
              $t('CONVERSATION.CALL_HISTORY.DURATION', {
                duration: formatDuration(call),
              })
            }}
          </p>
        </div>

        <div class="mt-3 flex flex-wrap items-center gap-2">
          <span
            class="rounded-full bg-n-alpha-2 px-2 py-1 text-xs font-medium text-n-slate-11"
            :data-testid="`whatsapp-call-history-status-${call.id}`"
          >
            {{ statusLabel(call.status) }}
          </span>
          <template v-if="call.recordingUrl">
            <Button
              v-tooltip.top="$t('CONVERSATION.CALL_HISTORY.PLAY')"
              :data-testid="`whatsapp-call-history-play-${call.id}`"
              ghost
              slate
              xs
              icon="i-ph-play-fill"
              :aria-label="$t('CONVERSATION.CALL_HISTORY.PLAY')"
              :disabled="isPlaying(call) || isLoadingPlayback(call)"
              @click="playCall(call)"
            />
            <Button
              v-tooltip.top="$t('CONVERSATION.CALL_HISTORY.PAUSE')"
              :data-testid="`whatsapp-call-history-pause-${call.id}`"
              ghost
              slate
              xs
              icon="i-ph-pause-fill"
              :aria-label="$t('CONVERSATION.CALL_HISTORY.PAUSE')"
              :disabled="!isPlaying(call)"
              @click="pauseCall(call)"
            />
            <Button
              v-tooltip.top="$t('CONVERSATION.CALL_HISTORY.STOP')"
              :data-testid="`whatsapp-call-history-stop-${call.id}`"
              ghost
              slate
              xs
              icon="i-ph-stop-fill"
              :aria-label="$t('CONVERSATION.CALL_HISTORY.STOP')"
              :disabled="!isActive(call)"
              @click="stopCall(call)"
            />
          </template>
          <span v-else class="text-xs text-n-slate-10">
            {{ $t('CONVERSATION.CALL_HISTORY.RECORDING_UNAVAILABLE') }}
          </span>
        </div>
      </li>
    </ul>

    <Button
      v-if="hasMoreCalls"
      class="mt-4 w-full"
      variant="faded"
      color="slate"
      :label="
        isLoadingMore
          ? $t('CONVERSATION.CALL_HISTORY.LOADING_MORE')
          : $t('CONVERSATION.CALL_HISTORY.LOAD_MORE')
      "
      :is-loading="isLoadingMore"
      :disabled="isLoadingMore"
      @click="loadMoreHistory"
    />

    <p v-if="hasPlaybackError" class="mt-3 text-xs text-n-ruby-11" role="alert">
      {{ $t('CONVERSATION.CALL_HISTORY.PLAYBACK_ERROR') }}
    </p>
  </section>
</template>
